import * as React from 'react';
import styled from 'styled-components';
import { Flex } from './compositeComponents/FlexHelpers';
import { Button } from './button/Button';
import { AnimatedCollapsibleContent } from './collapsible/AnimatedCollapsibleContent';

type PageSectionTableProps = {
    headerColumns:{
        text:string,
        gridDefinition:string,
    }[],
    noContentText:string,
    showAllLimit:number,
}


// The component assumes that children are a list of elements grouped in a react fragment that match the number of header columns, and that they are in the same order as the header columns.
export function PageSectionTable(props:React.PropsWithChildren<PageSectionTableProps>):JSX.Element
{
    const [showAll, setShowAll] = React.useState<boolean>(false);

    const allChildren: Array<React.ReactNode> = React.useMemo(() => props.children && Array.isArray(props.children) ? props.children : [], [props.children]);

    const firstBatch = allChildren.slice(0, props.showAllLimit);
    
    let secondBatch: Array<React.ReactNode> = [];
    if(allChildren.length > firstBatch.length){
        secondBatch = allChildren.slice(props.showAllLimit);
    }

    return (
        <Flex flexDirection="column" gap={6}>
            <RowHeader gridDefinitionColumns={props.headerColumns.map(c => c.gridDefinition)}>
                {props.headerColumns.map((c, i) => <span key={i}>{c.text}</span>)}
            </RowHeader>

            {firstBatch.length === 0 &&
                <Row gridDefinitionColumns={props.headerColumns.map(c => c.gridDefinition)}>
                    <span>{props.noContentText}</span>
                </Row>
            }

            {firstBatch.length > 0 && firstBatch.map((child, i) => 
                <Row key={i} gridDefinitionColumns={props.headerColumns.map(c => c.gridDefinition)}>{child}</Row>
            )}

            {secondBatch.length > 0 && !showAll &&
                <Flex justifyContent="center">
                    <Button variant="text" text="Vis alle" icon="arrows_chevron_chevronDown" onClick={() => setShowAll(true)} />
                </Flex>
            }
            
            <AnimatedCollapsibleContent open={showAll}>
                {showAll && secondBatch.map((child, i) => 
                    <Row key={i} gridDefinitionColumns={props.headerColumns.map(c => c.gridDefinition)}>{child}</Row>
                )}
            </AnimatedCollapsibleContent>
        </Flex>
    );
}

const Row = styled.div<{gridDefinitionColumns:string[]}>`
    align-items: center;
    background: ${props => props.theme.colors.activityCard.background};
    display: grid;
    grid-template-columns: ${props => props.gridDefinitionColumns.join(' ')};
    gap: 16px;
    border-bottom: 1px solid ${props => props.theme.colors.activityCard.border};
    padding: 0 8px 6px 8px;

    white-space: nowrap;  
`;

const RowHeader = styled(Row)`
    background: ${props => props.theme.colors.activityCard.headerBackground};
    color: ${props => props.theme.colors.activityCard.headerColor};
    padding: 8px;
    font-weight: bold;
    white-space: nowrap;
    border-bottom: 0;
`;